/**
 * @fileoverview Generic detail modal component with view/edit modes
 * Following DMS component patterns from the framework
 */
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { intl, DmsButton, DmsModal } from "dmsbasereact";
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";

/**
 * Generic detail modal component for viewing and editing entity details
 *
 * @example
 * <DmsDetailModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   data={patient}
 *   loading={loading}
 *   title={{ view: "Patient Details", edit: "Edit Patient" }}
 *   viewContent={<PatientViewContent patient={patient} />}
 *   editContent={<PatientEditContent formData={formData} onChange={handleChange} />}
 *   prepareFormData={(patient) => ({ name: patient.name, ... })}
 *   buildPayload={(formData) => ({ name: formData.name, ... })}
 *   onSave={handleSave}
 *   onDataUpdated={refreshTable}
 *   confirmMessage="Are you sure you want to save the changes?"
 * />
 */
export const DmsDetailModal = ({
  open,
  onClose,
  data = null,
  loading: externalLoading = false,
  title = { view: null, edit: null },
  viewContent: ViewContent,
  editContent: EditContent,
  prepareFormData,
  buildPayload = null,
  validateFormData,
  onSave,
  onDataUpdated = null,
  confirmTitle = null,
  confirmMessage = null,
  editContentProps = {},
  viewContentProps = {},
  canEdit = true,
  maxWidth = "md",
  startInEditMode = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasAutoEnteredEditMode, setHasAutoEnteredEditMode] = useState(false);

  // Local data that can be updated after save
  const [localData, setLocalData] = useState(null);

  // Update local data when prop changes
  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  // Auto-enter edit mode if startInEditMode is true and data is available
  useEffect(() => {
    if (startInEditMode && localData && prepareFormData && !hasAutoEnteredEditMode && !externalLoading) {
      const prepared = prepareFormData(localData);
      setFormData(prepared);
      setIsEditMode(true);
      setHasAutoEnteredEditMode(true);
    }
  }, [startInEditMode, localData, prepareFormData, hasAutoEnteredEditMode, externalLoading]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setFormData({});
      setLocalData(null);
      setHasAutoEnteredEditMode(false);
    }
  }, [open]);

  const handleEnterEditMode = useCallback(() => {
    if (!localData || !prepareFormData) return;
    const prepared = prepareFormData(localData);
    setFormData(prepared);
    setIsEditMode(true);
  }, [localData, prepareFormData]);

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setFormData({});
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!localData || !onSave) return;

    // Confirmation dialog before save
    const result = await Swal.fire({
      title: confirmTitle || intl.get("ConfirmSave").d("Confirm Save"),
      text: confirmMessage || intl.get("ConfirmSaveMessage").d("Are you sure you want to save the changes?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Confirm").d("Confirm"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    const payload = buildPayload ? buildPayload(formData, localData) : formData;
    const updatedData = await onSave(localData.pk, payload, formData);
    setSaving(false);

    if (updatedData) {
      // Update local state with new data
      setLocalData(updatedData);
      setIsEditMode(false);
      setFormData({});
      // Notify parent to refresh
      onDataUpdated?.();
    }
  }, [localData, formData, onSave, onDataUpdated, buildPayload, confirmTitle, confirmMessage]);

  const handleClose = useCallback(() => {
    setIsEditMode(false);
    setFormData({});
    onClose();
  }, [onClose]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isLoading = externalLoading;
  const showEditButton = canEdit !== false && !isEditMode && !isLoading && localData;

  // Validate form data if validator is provided
  const isFormValid = validateFormData ? validateFormData(formData) : true;

  const modalTitle = isEditMode
    ? (title?.edit || intl.get("EditDetails").d("Edit Details"))
    : (title?.view || intl.get("Details").d("Details"));

  return (
    <DmsModal
      isVisible={open}
      onClose={handleClose}
      maxWidth={maxWidth || "md"}
      fullWidth
      fullScreen={isMobile}
    >
      <DmsModal.Header
        title={modalTitle}
        onClose={handleClose}
      />
      <DmsModal.Body>
        <Box sx={{ p: isMobile ? 1 : 2 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            localData && (
              isEditMode ? (
                <EditContent
                  data={localData}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  {...editContentProps}
                />
              ) : (
                <ViewContent
                  data={localData}
                  {...viewContentProps}
                />
              )
            )
          )}
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 1,
            width: "100%",
            p: isMobile ? 1 : 2,
          }}
        >
          {isEditMode ? (
            <>
              <DmsButton
                icon={<CancelIcon />}
                text={intl.get("Cancel").d("Cancel")}
                onClick={handleCancelEditMode}
                disabled={saving}
                fullWidth={isMobile}
              />
              <DmsButton
                icon={<SaveIcon />}
                text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                onClick={handleSave}
                disabled={saving || !isFormValid}
                fullWidth={isMobile}
              />
            </>
          ) : (
            <>
              <DmsButton
                text={intl.get("Close").d("Close")}
                onClick={handleClose}
                fullWidth={isMobile}
              />
              {showEditButton && (
                <DmsButton
                  icon={<EditIcon />}
                  text={intl.get("Edit").d("Edit")}
                  onClick={handleEnterEditMode}
                  fullWidth={isMobile}
                />
              )}
            </>
          )}
        </Box>
      </DmsModal.Footer>
    </DmsModal>
  );
};

DmsDetailModal.propTypes = {
  /** Modal visibility */
  open: PropTypes.bool.isRequired,
  /** Close handler */
  onClose: PropTypes.func.isRequired,
  /** Entity data object (must have pk property) */
  data: PropTypes.object,
  /** External loading state */
  loading: PropTypes.bool,
  /** Modal titles for view and edit modes */
  title: PropTypes.shape({
    view: PropTypes.string,
    edit: PropTypes.string,
  }),
  /** Component to render in view mode - receives { data, ...viewContentProps } */
  viewContent: PropTypes.elementType.isRequired,
  /** Component to render in edit mode - receives { data, formData, onFieldChange, ...editContentProps } */
  editContent: PropTypes.elementType.isRequired,
  /** Function to prepare form data from entity data */
  prepareFormData: PropTypes.func.isRequired,
  /** Function to build payload from form data for save */
  buildPayload: PropTypes.func,
  /** Save handler - receives (pk, payload, formData), should return updated data or null */
  onSave: PropTypes.func.isRequired,
  /** Callback when data is successfully updated */
  onDataUpdated: PropTypes.func,
  /** Custom confirmation dialog title */
  confirmTitle: PropTypes.string,
  /** Custom confirmation dialog message */
  confirmMessage: PropTypes.string,
  /** Additional props to pass to edit content component */
  editContentProps: PropTypes.object,
  /** Additional props to pass to view content component */
  viewContentProps: PropTypes.object,
  /** Whether edit button should be shown (default: true) */
  canEdit: PropTypes.bool,
  /** Modal max width */
  maxWidth: PropTypes.string,
  /** Whether to start in edit mode automatically */
  startInEditMode: PropTypes.bool,
};


export default DmsDetailModal;
